// Mapping of role IDs to their custom display names
export const ROLE_DISPLAY_NAMES = {
  BCBA: 'Board Certified Behavioral Analyst (BCBA)',
  'Occupational Therapist': 'Occupational Therapist',
  'Speech and Language Pathologist': 'Speech and Language Pathologist',
  'Special Education Teacher': 'Special Education Teacher',
  Psychologist: 'Psychologist',
  'Physical Therapist': 'Physical Therapist',
  'Mental Health Clinician': 'Mental Health Clinician',
  RBT: 'Registered Behavior Technician (RBT)',
  'Residential Staff': 'Residential Staff',
  Nurse: 'Nurse',
  'Classroom Aide': 'Classroom Aide',
  'Art Therapist': 'Art Therapist',
};

/**
 * Safely convert a subRole value into a displayable string.
 * Accepts strings, role objects, or arrays mixing both.
 * @param {string|Object|Array} subRole
 * @returns {string}
 */
export const formatSubRoleLabel = (subRole) => {
  if (!subRole) {
    return '';
  }

  if (Array.isArray(subRole)) {
    return subRole
      .map((role) => formatSubRoleLabel(role))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof subRole === 'string') {
    return subRole;
  }

  if (typeof subRole === 'object') {
    return (
      subRole.permissions?.customRoleName ||
      subRole.name ||
      subRole.label ||
      subRole.id ||
      ''
    );
  }

  return '';
};

// Priority order for roles
export const PRIORITY_ROLES = [
  'BCBA',
  'Occupational Therapist',
  'Speech and Language Pathologist',
  'Special Education Teacher',
  'Psychologist',
  'Physical Therapist',
  'Mental Health Clinician',
  'RBT',
  'Residential Staff',
  'Nurse',
  'Classroom Aide',
  'Art Therapist',
  'Other',
];

/**
 * Get display name for a role
 * @param {Object} role - Role object with id, name, etc.
 * @returns {string} Display name for the role
 */
export const getRoleDisplayName = (role) => {
  if (!role) return '';

  // For custom roles, use customRoleName
  if (role.permissions?.customRoleName) {
    return role.permissions.customRoleName;
  }

  // Check if we have a custom display name mapping
  const roleId = role.id;
  if (ROLE_DISPLAY_NAMES[roleId]) {
    return ROLE_DISPLAY_NAMES[roleId];
  }

  // Fallback to name or id
  return role.name || role.id || '';
};

/**
 * Sort and organize roles for dropdown display
 * Priority roles first, then "Other" separator, then rest of the roles
 * @param {Array} roles - Array of role objects
 * @returns {Array} Sorted and organized roles array with separator markers
 */
export const organizeRolesForDropdown = (roles) => {
  if (!Array.isArray(roles)) return [];

  const priorityRoles = [];
  const otherRoles = [];
  const customRoles = [];
  let otherRole = null; // Extract "Other" role separately

  roles.forEach((role) => {
    const roleId = role.id;

    // Extract "Other" role separately
    if (roleId === 'Other') {
      otherRole = role;
      return; // Don't add to other arrays
    }

    // Custom roles always go to the end
    if (role.roleType === 'custom') {
      customRoles.push(role);
    }
    // Check if it's a priority role (excluding "Other" which we handle separately)
    else if (PRIORITY_ROLES.includes(roleId) && roleId !== 'Other') {
      priorityRoles.push(role);
    } else {
      otherRoles.push(role);
    }
  });

  // Sort priority roles by PRIORITY_ROLES order (excluding "Other")
  priorityRoles.sort((a, b) => {
    const indexA = PRIORITY_ROLES.indexOf(a.id);
    const indexB = PRIORITY_ROLES.indexOf(b.id);
    return indexA - indexB;
  });

  otherRoles.sort((a, b) => {
    const nameA = getRoleDisplayName(a).toLowerCase();
    const nameB = getRoleDisplayName(b).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Sort custom roles alphabetically
  customRoles.sort((a, b) => {
    const nameA = getRoleDisplayName(a).toLowerCase();
    const nameB = getRoleDisplayName(b).toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Insert "Other" right after "Art Therapist" in priority roles
  const artTherapistIndex = priorityRoles.findIndex(
    (r) => r.id === 'Art Therapist'
  );
  if (artTherapistIndex !== -1 && otherRole) {
    // Insert "Other" right after "Art Therapist"
    priorityRoles.splice(artTherapistIndex + 1, 0, otherRole);
  } else if (otherRole) {
    // If "Art Therapist" not found, add "Other" at the end of priority roles
    priorityRoles.push(otherRole);
  }

  const organizedRoles = [...priorityRoles, ...otherRoles, ...customRoles];

  return organizedRoles;
};

/**
 * Check if a role is in the priority list
 * @param {string} roleId - Role ID to check
 * @returns {boolean} True if role is in priority list
 */
export const isPriorityRole = (roleId) => {
  return PRIORITY_ROLES.includes(roleId);
};
